import {
  UserAccount,
  GroupMemberProfile,
  GroupSettings,
  Venue,
  Match,
  MatchRSVP,
  MatchJoinRequest,
  PaymentRecord,
  MatchComment,
  Announcement,
  Poll,
  ActivityLog,
  NotificationSettings,
  PrivacySettings,
  RSVPStatus,
  FootballPosition,
  CardEditMode
} from '../types';

import {
  INITIAL_USER,
  INITIAL_GROUP,
  INITIAL_MEMBERS,
  INITIAL_VENUES,
  INITIAL_MATCHES,
  INITIAL_RSVPS,
  INITIAL_JOIN_REQUESTS,
  INITIAL_PAYMENTS,
  INITIAL_COMMENTS,
  INITIAL_ANNOUNCEMENTS,
  INITIAL_POLLS,
  INITIAL_ACTIVITY_LOGS,
  INITIAL_NOTIFICATIONS_SETTINGS,
  INITIAL_PRIVACY_SETTINGS,
  INITIAL_WORLD_FIXTURES,
  INITIAL_WORLD_NEWS
} from '../data/seedData';

export interface AppState {
  activeUserId: string;
  user: UserAccount;
  group: GroupSettings;
  members: GroupMemberProfile[];
  venues: Venue[];
  matches: Match[];
  rsvps: MatchRSVP[];
  joinRequests: MatchJoinRequest[];
  payments: PaymentRecord[];
  comments: MatchComment[];
  announcements: Announcement[];
  polls: Poll[];
  activityLogs: ActivityLog[];
  notificationSettings: NotificationSettings;
  privacySettings: PrivacySettings;
  worldFixtures: any[];
  worldNews: any[];
}

export function getInitialState(): AppState {
  return {
    activeUserId: INITIAL_USER.id,
    user: INITIAL_USER,
    group: INITIAL_GROUP,
    members: INITIAL_MEMBERS,
    venues: INITIAL_VENUES,
    matches: INITIAL_MATCHES,
    rsvps: INITIAL_RSVPS,
    joinRequests: INITIAL_JOIN_REQUESTS,
    payments: INITIAL_PAYMENTS,
    comments: INITIAL_COMMENTS,
    announcements: INITIAL_ANNOUNCEMENTS,
    polls: INITIAL_POLLS,
    activityLogs: INITIAL_ACTIVITY_LOGS,
    notificationSettings: INITIAL_NOTIFICATIONS_SETTINGS,
    privacySettings: INITIAL_PRIVACY_SETTINGS,
    worldFixtures: INITIAL_WORLD_FIXTURES,
    worldNews: INITIAL_WORLD_NEWS,
  };
}

export function saveState(state: AppState) {
  fetch('/api/state', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(state),
  }).catch((error) => console.warn('Failed to persist state to SQLite:', error));
}

// Helpers
export function getActiveMember(state: AppState): GroupMemberProfile | undefined {
  return state.members.find(m => m.userId === state.activeUserId);
}

export function getMemberByUserId(state: AppState, userId: string): GroupMemberProfile | undefined {
  return state.members.find(m => m.userId === userId);
}

export function getMemberById(state: AppState, memberId: string): GroupMemberProfile | undefined {
  return state.members.find(m => m.id === memberId);
}

// Smart Auto Team Balancer Algorithm
export function autoBalanceTeams(
  confirmedMembers: GroupMemberProfile[],
  formationA: string = '2-3-1',
  formationB: string = '2-3-1'
) {
  // Sort members by overall rating descending
  const sorted = [...confirmedMembers].sort((a, b) => b.footballCard.overall - a.footballCard.overall);
  
  const teamAPlayers: GroupMemberProfile[] = [];
  const teamBPlayers: GroupMemberProfile[] = [];

  // Separate Goalkeepers first if present
  const gks = sorted.filter(m => m.primaryPosition === 'GK' || m.goalkeeperAbility === 'Yes');
  const outfield = sorted.filter(m => !gks.includes(m));

  // Distribute GKs
  if (gks.length >= 2) {
    teamAPlayers.push(gks[0]);
    teamBPlayers.push(gks[1]);
    outfield.push(...gks.slice(2));
  } else if (gks.length === 1) {
    teamAPlayers.push(gks[0]);
    outfield.push(...gks.slice(1));
  }

  // Snake draft distribution for outfield to keep total ratings balanced
  let toA = teamAPlayers.length <= teamBPlayers.length;
  outfield.forEach(player => {
    if (toA) {
      teamAPlayers.push(player);
    } else {
      teamBPlayers.push(player);
    }
    toA = !toA;
  });

  // Assign pitch coordinates for 2-3-1 layout
  const mapPitch = (players: GroupMemberProfile[], formation: string) => {
    const layoutPositions: { pos: FootballPosition; x: number; y: number }[] = [
      { pos: 'GK', x: 50, y: 88 },
      { pos: 'CB', x: 35, y: 72 },
      { pos: 'RB', x: 65, y: 72 },
      { pos: 'CM', x: 50, y: 50 },
      { pos: 'LM', x: 22, y: 45 },
      { pos: 'RM', x: 78, y: 45 },
      { pos: 'ST', x: 50, y: 22 },
    ];

    return players.map((p, idx) => {
      const slot = layoutPositions[idx] || { pos: p.primaryPosition, x: 20 + (idx * 15) % 80, y: 30 + (idx * 20) % 60 };
      return {
        memberProfileId: p.id,
        position: p.primaryPosition || slot.pos,
        pitchX: slot.x,
        pitchY: slot.y,
      };
    });
  };

  return {
    teamA: {
      name: 'Team Green',
      color: '#10B981',
      formation: formationA,
      players: mapPitch(teamAPlayers, formationA)
    },
    teamB: {
      name: 'Team Blue',
      color: '#3B82F6',
      formation: formationB,
      players: mapPitch(teamBPlayers, formationB)
    }
  };
}
